import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTrash, faShoppingCart } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import Swal from "sweetalert2";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "sweetalert2/src/sweetalert2.scss";
import "../App.css";
import {
  updateCartProducts,
  ExecApiDetailOrderWhatsap,
  checkPhoneclient,
} from "./api";
import {
  customStyles,
  StyledInput,
} from "../customStyles";
import { calculateCartTotal } from "./cartTotal";
import {
  getProducts,
  getDiscountProducts,
  searchProducts,
  getProductAvailability,
} from "./ApiProducts";
import { hasActiveDiscount } from "./discountUtils";

Modal.setAppElement("#root");

const PAGE_SIZE = parseInt(process.env.REACT_APP_PAGE_SIZE || "50", 10);
const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";
const DEV_WHATSAPP_PHONE = String(
  process.env.REACT_APP_DEV_WHATSAPP_PHONE || ""
)
  .replace(/[^\d]/g, "")
  .trim();
const MOBILE_BREAKPOINT_QUERY = "(max-width: 640px)";
const productImageModalStyles = {
  overlay: {
    backgroundColor: "rgba(15, 23, 42, 0.82)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "2100",
    padding: "20px",
  },
  content: {
    inset: "auto",
    position: "relative",
    width: "min(96vw, 980px)",
    maxWidth: "980px",
    maxHeight: "92vh",
    margin: "0",
    padding: "16px",
    border: "none",
    borderRadius: "24px",
    background: "transparent",
    overflow: "visible",
    boxShadow: "none",
  },
};

const getCategoryIdentity = (category) =>
  String(category?.category_id ?? category?.app_category_id ?? "");

const normalizeCategoryFilter = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getCategoryName = (category) =>
  String(category?.name ?? category?.category_name ?? category?.label ?? "")
    .trim()
    .toLowerCase();

const formatCOP = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getMinimumOrderValue = (sessionData) => {
  const candidates = [
    sessionData?.min_delivery,
    sessionData?.cart?.min_delivery,
    sessionData?.result?.min_delivery,
    sessionData?.sede?.min_delivery,
    sessionData?.cart?.sede?.min_delivery,
    sessionData?.result?.sede?.min_delivery,
  ];

  const resolved = candidates.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );

  const parsed = Number(resolved);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getProductIdentity = (product) =>
  String(product.app_item_id ?? product.item_id ?? product.id ?? "");

const dedupeProducts = (items) => {
  const seen = new Set();

  return items.filter((item) => {
    const identity = getProductIdentity(item);
    const fallbackIdentity = `${identity}-${item.item_name ?? ""}-${item.category_name ?? ""}`;

    if (seen.has(fallbackIdentity)) {
      return false;
    }

    seen.add(fallbackIdentity);
    return true;
  });
};

/**
 * Calculate the discounted price for a product.
 * Returns the original price if no discount applies.
 */
const getDiscountedPrice = (product) => {
  if (hasActiveDiscount(product)) {
    return product.price * (1 - product.discount / 100);
  }
  return product.price;
};

const getHydratedCartPricing = (remoteItem, matchedProduct) => {
  const matchedBasePrice = toFiniteNumber(matchedProduct?.price);
  const remoteBasePriceCandidates = [
    remoteItem?.original_price,
    remoteItem?.base_price,
    remoteItem?.price,
  ];
  const remoteBasePrice =
    remoteBasePriceCandidates
      .map(toFiniteNumber)
      .find((value) => value !== null && value >= 0) ?? null;
  const remoteUnitPrice = toFiniteNumber(remoteItem?.price_unit);

  // The persisted cart (remoteItem) is the source of truth for what the
  // customer will actually be charged, so its discount takes priority over
  // whatever the live catalog currently reports for this barcode.
  const remoteDiscount = toFiniteNumber(remoteItem?.discount);
  const remoteDiscountType =
    remoteItem?.discount_type != null
      ? String(remoteItem.discount_type).trim()
      : "porcentaje";
  const hasRemoteDiscount =
    remoteDiscount !== null &&
    remoteDiscount > 0 &&
    remoteDiscountType === "porcentaje";

  if (hasRemoteDiscount) {
    // price_unit in the cart payload is already the original (pre-discount)
    // unit price — subtotal = price_unit * quantity * (1 - discount / 100).
    const derivedBasePrice =
      remoteBasePrice !== null
        ? remoteBasePrice
        : remoteUnitPrice !== null
          ? remoteUnitPrice
          : matchedBasePrice;

    if (derivedBasePrice !== null) {
      return {
        price: derivedBasePrice,
        discount: remoteDiscount,
        discount_type: remoteDiscountType,
        discount_start_date:
          remoteItem?.discount_start_date ?? matchedProduct?.discount_start_date,
        discount_end_date:
          remoteItem?.discount_end_date ?? matchedProduct?.discount_end_date,
      };
    }
  }

  if (matchedProduct && matchedBasePrice !== null) {
    return {
      price: matchedBasePrice,
      discount: matchedProduct.discount,
      discount_type: matchedProduct.discount_type,
      discount_start_date: matchedProduct.discount_start_date,
      discount_end_date: matchedProduct.discount_end_date,
    };
  }

  return {
    price:
      remoteUnitPrice ??
      remoteBasePrice ??
      matchedBasePrice ??
      0,
    discount: 0,
    discount_type: null,
    discount_start_date: null,
    discount_end_date: null,
  };
};

const normalizeAvailableQty = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const getItemIdentity = (item) =>
  String(item?.app_item_id ?? item?.item_id ?? item?.id ?? "").trim();

const getBarcodeIdentity = (item) =>
  String(item?.item_barcode ?? item?.barcode ?? "").trim();

const getAddonSelectionKey = (cartItem, addonItem) =>
  `${String(cartItem?.item_id ?? cartItem?.app_item_id ?? "")}-${String(
    addonItem?.app_item_id ?? addonItem?.item_id ?? addonItem?.id ?? ""
  )}`;

const getItemDisplayName = (item) =>
  item?.item_name ?? item?.product_name ?? item?.name_modifier_groups ?? "Producto";

const ProductMenu = ({
  categories,
  sedeId,
  sessionId,
  sessionData,
  initialCategoryFilter = "",
}) => {
  // ── Product state (loaded from API, server-side paginated) ──
  const [products, setProducts] = useState([]);
  const [discountProducts, setDiscountProducts] = useState([]);
  const [discountCatalogLoaded, setDiscountCatalogLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiPage, setApiPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // ── Category state ──
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);

  // ── Search state ──
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchKey, setSearchKey] = useState(0);

  // ── Cart state ──
  const [cart, setCart] = useState([]);
  const [isChecked, setIsChecked] = useState({});
  const [selectedAcompanantes, setSelectedAcompanantes] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [selectedQuantitiesV2, setSelectedQuantitiesV2] = useState({});
  const [acompanantesOptions, setAcompanantesOptions] = useState([]);
  const [selections, setSelections] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedRadio, setSelectedRadio] = useState([]);
  const [error, setError] = useState(false);
  const [minimumOrderError, setMinimumOrderError] = useState(false);
  const [totalSelected, setTotalSelected] = useState(0);
  const [unavailableAddons, setUnavailableAddons] = useState({});
  const [addonStockMessage, setAddonStockMessage] = useState("");
  const [cartStockErrorMessage, setCartStockErrorMessage] = useState("");
  const [adjustedCartItems, setAdjustedCartItems] = useState(new Set());

  // ── UI state ──
  const REDIRECT_WHATSAPP = process.env.REACT_APP_REDIRECT_WHATSAPP;
  const [modalIsOpen, setIsOpen] = useState(false);
  const [selectedImageProduct, setSelectedImageProduct] = useState(null);
  const [shouldBlink, setShouldBlink] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [hasConfirmedWhatsAppOrder, setHasConfirmedWhatsAppOrder] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // ── Category bar scroll ──
  const categoryBarRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const searchRequestRef = useRef(0);
  const hasHydratedCartRef = useRef(false);
  const cartSyncTimeoutRef = useRef(null);
  const cartSyncRequestIdRef = useRef(0);
  const [cartSyncVersion, setCartSyncVersion] = useState(0);
  const [hasUserTouchedCart, setHasUserTouchedCart] = useState(false);
  const [isAutoSyncingCart, setIsAutoSyncingCart] = useState(false);
  const [expandedNoteItemId, setExpandedNoteItemId] = useState(null);
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches;
  });

  const markCartSyncDirty = useCallback(() => {
    setHasUserTouchedCart(true);
    setHasConfirmedWhatsAppOrder(false);
    setCartSyncVersion((prev) => prev + 1);
  }, []);

  const updateScrollState = useCallback(() => {
    const el = categoryBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  }, []);

  useEffect(() => {
    const el = categoryBarRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [categories, updateScrollState]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const handleChange = (event) => setIsMobileView(event.matches);

    setIsMobileView(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    const el = categoryBarRef.current;

    if (!el || !selectedCategory) {
      return;
    }

    const activeCategory = el.querySelector(
      `[data-category-id="${String(selectedCategory)}"]`
    );

    if (!activeCategory) {
      return;
    }

    activeCategory.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [selectedCategory]);

  const scrollCategories = (direction) => {
    const el = categoryBarRef.current;
    if (!el) return;
    const scrollAmount = 200;
    el.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // ── Auto-select first category on mount ──
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      const normalizedFilter = normalizeCategoryFilter(initialCategoryFilter);
      const matchedCategory = normalizedFilter
        ? categories.find((category) => {
            const categoryIdentity = getCategoryIdentity(category).toLowerCase();
            const categoryName = getCategoryName(category);

            return (
              categoryIdentity === normalizedFilter ||
              categoryName === normalizedFilter
            );
          })
        : null;

      setSelectedCategory(getCategoryIdentity(matchedCategory || categories[0]));
    }
  }, [categories, selectedCategory, initialCategoryFilter]);

  useEffect(() => {
    if (!sedeId) {
      return;
    }

    let isMounted = true;

    const fetchDiscountProducts = async () => {
      setDiscountCatalogLoaded(false);
      const result = await getDiscountProducts(sedeId);

      if (!isMounted) {
        return;
      }

      setDiscountProducts(dedupeProducts(result.products || []));
      setDiscountCatalogLoaded(true);
    };

    fetchDiscountProducts();

    return () => {
      isMounted = false;
    };
  }, [sedeId]);

  useEffect(() => {
    const remoteCartProducts = Array.isArray(sessionData?.products)
      ? sessionData.products
      : [];

    if (
      hasHydratedCartRef.current ||
      remoteCartProducts.length === 0 ||
      !discountCatalogLoaded
    ) {
      return;
    }

    const hydratedCart = remoteCartProducts.map((remoteItem) => {
      const remoteBarcode = String(remoteItem?.barcode ?? "").trim();
      const matchedProduct =
        products.find(
          (product) => getBarcodeIdentity(product) === remoteBarcode
        ) ||
        discountProducts.find(
          (product) => getBarcodeIdentity(product) === remoteBarcode
        ) || null;
      const hydratedPricing = getHydratedCartPricing(remoteItem, matchedProduct);

      return {
        ...(matchedProduct || {}),
        item_id:
          matchedProduct?.item_id ??
          matchedProduct?.app_item_id ??
          remoteItem?.product_id ??
          remoteItem?.id ??
          remoteBarcode,
        app_item_id:
          matchedProduct?.app_item_id ??
          matchedProduct?.item_id ??
          remoteItem?.product_id ??
          remoteItem?.id ??
          remoteBarcode,
        item_name:
          matchedProduct?.item_name ??
          remoteItem?.product_name ??
          "Producto",
        item_barcode: remoteBarcode,
        price: hydratedPricing.price,
        discount: hydratedPricing.discount,
        discount_type: hydratedPricing.discount_type,
        discount_start_date: hydratedPricing.discount_start_date,
        discount_end_date: hydratedPricing.discount_end_date,
        quantity: Math.max(1, Number(remoteItem?.quantity) || 1),
        note: remoteItem?.note || "",
        modifier_groups: matchedProduct?.modifier_groups || [],
        subproducts: matchedProduct?.subproducts || [],
        acompanantes: matchedProduct?.acompanantes || [],
      };
    });

    setCart(hydratedCart);
    hasHydratedCartRef.current = true;

    console.info("[Pedido] Carrito hidratado desde la sesion", {
      sessionId,
      remoteProducts: remoteCartProducts.length,
      hydratedProducts: hydratedCart.length,
    });
  }, [sessionData, products, discountProducts, discountCatalogLoaded, sessionId]);

  // ── Fetch products when category or page changes ──
  useEffect(() => {
    if (!selectedCategory || !sedeId) return;

    const selectedCategoryData =
      categories.find(
        (category) => getCategoryIdentity(category) === String(selectedCategory)
      ) ?? selectedCategory;

    const fetchProducts = async () => {
      setIsLoading(true);
      const result = await getProducts(
        sedeId,
        apiPage,
        PAGE_SIZE,
        selectedCategoryData
      );
      setProducts((prevProducts) => {
        const nextProducts = dedupeProducts(result.products || []);

        if (isMobileView && apiPage > 1) {
          return dedupeProducts([...prevProducts, ...nextProducts]);
        }

        return nextProducts;
      });
      setTotalPages(result.totalPages);
      setTotalProducts(result.total);
      setHasNext(result.hasNext);
      setHasPrev(result.hasPrev);
      setIsLoading(false);

      if (!isMobileView || apiPage === 1) {
        // Scroll to top of products section
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    fetchProducts();
  }, [selectedCategory, apiPage, sedeId, categories, isMobileView]);

  // ── Category change handler ──
  const handleCategoryChange = (categoryId) => {
    if (categoryId === selectedCategory && !isSearchMode) return;

    // Clear old products immediately so stale data doesn't flash
    setProducts([]);
    setIsLoading(true);

    setSearchTerm("");
    setIsSearchMode(false);
    setSearchResults([]);

    if (categoryId === selectedCategory) {
      // Same category after search: force re-fetch by toggling apiPage
      setApiPage(0);
      setTimeout(() => setApiPage(1), 0);
    } else {
      setSelectedCategory(categoryId);
      setApiPage(1);
    }

    setIsCategoryMenuOpen(false);
  };

  // ── Page navigation ──
  const goToNextPage = () => {
    if (hasNext) setApiPage((prev) => prev + 1);
  };
  const goToPrevPage = () => {
    if (hasPrev) setApiPage((prev) => prev - 1);
  };
  const goToPage = (pageNumber) => {
    setApiPage(pageNumber);
  };

  const loadMoreProducts = () => {
    if (hasNext && !isLoading) {
      setApiPage((prev) => prev + 1);
    }
  };

  // ── Debounced semantic search ──
  useEffect(() => {
    const normalizedTerm = searchTerm.trim();

    if (!normalizedTerm || normalizedTerm.length < 2) {
      searchRequestRef.current += 1;
      setIsSearchMode(false);
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Enter search mode immediately so category products are hidden
    setIsSearchMode(true);
    setIsSearching(true);
    // Clear previous results immediately so stale data doesn't persist
    setSearchResults([]);

    const requestId = ++searchRequestRef.current;
    const debounceTimer = setTimeout(async () => {
      const results = await searchProducts(normalizedTerm, sedeId, 20);

      if (requestId !== searchRequestRef.current) {
        return;
      }

      setSearchResults(dedupeProducts(results || []));
      setIsSearching(false);
    }, 400);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchTerm, sedeId, searchKey]);

  // ── Display products: search results or paginated category products ──
  const displayProducts = isSearchMode ? searchResults : products;
  const minimumOrderValue = getMinimumOrderValue(sessionData);
  const selectedCategoryLabel =
    categories.find(
      (category) => getCategoryIdentity(category) === String(selectedCategory)
    )?.name || "Selecciona una categoría";

  const findKnownProductById = useCallback(
    (itemId) => {
      const normalizedItemId = String(itemId ?? "").trim();

      if (!normalizedItemId) {
        return null;
      }

      const candidateLists = [products, discountProducts, searchResults, cart];

      for (const list of candidateLists) {
        const match = list.find(
          (product) => getItemIdentity(product) === normalizedItemId
        );

        if (match) {
          return match;
        }
      }

      return null;
    },
    [products, discountProducts, searchResults, cart]
  );

  const resolveAddonProduct = useCallback(
    (cartItem, addonItem) => {
      const addonId = getItemIdentity(addonItem);

      return (
        cartItem?.acompanantes?.find(
          (acompanante) => getItemIdentity(acompanante) === addonId
        ) ||
        findKnownProductById(addonId) ||
        addonItem ||
        null
      );
    },
    [findKnownProductById]
  );

  const ensureAddonAvailability = useCallback(
    async (cartItem, addonItem) => {
      const addonSelectionKey = getAddonSelectionKey(cartItem, addonItem);
      const resolvedAddon = resolveAddonProduct(cartItem, addonItem);
      const barcode = getBarcodeIdentity(resolvedAddon);
      if (!barcode) {
        console.info(
          "[Pedido] Acompanamiento sin barcode; se difiere validacion estricta al checkout",
          {
            addon: resolvedAddon,
          }
        );
        return {
          available: true,
          addon: resolvedAddon,
          qtyAvailable: Number.MAX_SAFE_INTEGER,
        };
      }

      console.info("[Pedido] Validando existencias de acompanamiento", {
        itemId: getItemIdentity(resolvedAddon),
        barcode,
        sedeId,
      });

      const realtimeAddon = await getProductAvailability(barcode, sedeId);
      const realtimeQty = normalizeAvailableQty(realtimeAddon?.qty_available);

      if (realtimeQty <= 0) {
        setUnavailableAddons((prev) => ({ ...prev, [addonSelectionKey]: true }));
        setAddonStockMessage(
          `${getItemDisplayName(resolvedAddon)} no tiene existencias disponibles en este momento.`
        );
        Swal.fire({
          icon: "warning",
          title: "Sin disponibilidad",
          text: "Este acompañamiento no tiene existencias disponibles en este momento.",
          confirmButtonColor: "#e63946",
        });
        return {
          available: false,
          addon: {
            ...resolvedAddon,
            ...(realtimeAddon || {}),
          },
          qtyAvailable: 0,
        };
      }

      setUnavailableAddons((prev) => ({ ...prev, [addonSelectionKey]: false }));
      setAddonStockMessage("");
      return {
        available: true,
        addon: {
          ...resolvedAddon,
          ...(realtimeAddon || {}),
          qty_available: realtimeQty,
        },
        qtyAvailable: realtimeQty,
      };
    },
    [resolveAddonProduct, sedeId]
  );

  // ── Clear search handler ──
  const clearSearch = () => {
    searchRequestRef.current += 1;
    setSearchTerm("");
    setIsSearchMode(false);
    setSearchResults([]);
    setSearchKey((k) => k + 1);
  };

  // ── Cart calculation ──
  const total = calculateCartTotal(cart, selectedQuantities, products);
  const formattedTotal = formatCOP(total);

  useEffect(() => {
    if (
      minimumOrderError &&
      (!minimumOrderValue || total >= minimumOrderValue)
    ) {
      setMinimumOrderError(false);
    }
  }, [total, minimumOrderError, minimumOrderValue]);


  // ── Modifier groups helpers ──
  const handleAcompananteChange = (itemId, acompananteId) => {
    setSelectedAcompanantes((prev) => ({
      ...prev,
      [itemId]: acompananteId,
    }));
  };

  /**
   * Check if a product has modifier groups (acompañamientos).
   */
  const hasModifierGroups = (product) => {
    return (
      Array.isArray(product.modifier_groups) &&
      product.modifier_groups.length > 0
    );
  };

  /**
   * Check if a product has subproducts (new API format for acompañamientos).
   */
  const hasSubproducts = (product) => {
    return (
      Array.isArray(product.subproducts) &&
      product.subproducts.length > 0
    );
  };

  useEffect(() => {
    if (selectedProduct && hasModifierGroups(selectedProduct)) {
      const modifierGroups = selectedProduct.modifier_groups || [];
      const newAcompanantesOptions = modifierGroups.flatMap(
        (group) => group.app_mg_items || []
      );
      setAcompanantesOptions(newAcompanantesOptions);
    }
  }, [selectedProduct]);

  const obtenerAcompanantesParaProducto = (producto, items, modifierGroups) => {
    if (!Array.isArray(modifierGroups) || !hasModifierGroups(producto)) {
      return [];
    }
    const gruposModificadores = modifierGroups.filter((group) =>
      producto.modifier_groups.some(
        (mg) => mg.app_modifier_group_id === group.app_modifier_group_id
      )
    );
    if (!gruposModificadores.length) return [];
    let acompanantes = [];
    gruposModificadores.forEach((grupo) => {
      const acompanantesGrupo = items.filter((item) =>
        grupo.app_mg_items.some(
          (mg_item) => mg_item.app_item_id === item.app_item_id
        )
      );
      acompanantes = [...acompanantes, ...acompanantesGrupo];
    });
    return acompanantes;
  };

  // ── Add to Cart ──
  const addToCart = async (productId, quantity) => {
    setIsAddingToCart(true);

    try {
      const selectedProd =
        products.find((product) => product.item_id === productId) ||
        searchResults.find((product) => product.item_id === productId);
      if (!selectedProd) return;

      const realtimeProduct = await getProductAvailability(
        selectedProd.item_barcode,
        sedeId
      );
      const realtimeQty = normalizeAvailableQty(realtimeProduct?.qty_available);
      const requestedQty = Math.max(1, Number(quantity) || 1);
      const existingCartQty =
        cart.find((product) => product.item_id === productId)?.quantity || 0;
      const totalRequestedQty = existingCartQty + requestedQty;
      const remainingQty = Math.max(realtimeQty - existingCartQty, 0);
      const hasRealtimeStock = realtimeProduct && realtimeQty > 0;

      if (!hasRealtimeStock) {
        Swal.fire({
          icon: "warning",
          title: "Sin disponibilidad",
          text: "En este momento el producto no tiene existencias disponibles.",
          confirmButtonColor: "#e63946",
        });
        return;
      }

      if (totalRequestedQty > realtimeQty) {
        Swal.fire({
          icon: "warning",
          title: "Stock insuficiente",
          text:
            remainingQty === 1
              ? "Solo puedes agregar 1 unidad mas de este producto."
              : remainingQty > 1
                ? `Solo puedes agregar ${remainingQty} unidades mas en este momento.`
                : "Ya no hay unidades disponibles para agregar de este producto.",
          confirmButtonColor: "#e63946",
        });
        return;
      }

      const existingProductIndex = cart.findIndex(
        (product) => product.item_id === productId
      );

      if (existingProductIndex >= 0) {
        const updatedCart = [...cart];
        updatedCart[existingProductIndex].quantity += requestedQty;
        updatedCart[existingProductIndex].qty_available = realtimeQty;
        setCart(updatedCart);
        markCartSyncDirty();
      } else {
        // Build product for cart with optional modifier groups
        const newProduct = {
          ...selectedProd,
          quantity: requestedQty,
          qty_available: realtimeQty,
          acompanantes: hasModifierGroups(selectedProd)
            ? obtenerAcompanantesParaProducto(
                selectedProd,
                products,
                selectedProd.modifier_groups
              )
            : [],
          modifier_groups: selectedProd.modifier_groups || [],
          subproducts: selectedProd.subproducts || [],
        };

        setCart([...cart, newProduct]);
        markCartSyncDirty();

        if (hasModifierGroups(selectedProd)) {
          const firstGroup = selectedProd.modifier_groups[0];
          setAcompanantesOptions(firstGroup?.app_mg_items || []);
        }
      }

      setShouldBlink(true);
      setTimeout(() => setShouldBlink(false), 2000);

      Swal.fire({
        icon: "success",
        title: "¡Añadido!",
        showConfirmButton: false,
        timer: 800,
        toast: true,
        position: "top-end",
        customClass: { popup: "swal2-toast-custom" },
      });
    } finally {
      setTimeout(() => setIsAddingToCart(false), 1500);
    }
  };

  // ── Remove from Cart ──
  const removeFromCart = (productId) => {
    const updatedCart = cart.filter((item) => item.item_id !== productId);
    setCart(updatedCart);
    markCartSyncDirty();
  };

  const incrementCartItemQuantity = async (productId) => {
    const cartItem = cart.find((item) => item.item_id === productId);
    if (!cartItem) return;

    const realtimeProduct = await getProductAvailability(
      cartItem.item_barcode,
      sedeId
    );
    const realtimeQty = normalizeAvailableQty(realtimeProduct?.qty_available);
    const currentQty = Math.max(1, Number(cartItem.quantity) || 1);

    if (realtimeQty <= currentQty) {
      Swal.fire({
        icon: "warning",
        title: "Stock insuficiente",
        text: "No hay mas existencias disponibles para este producto.",
        confirmButtonColor: "#e63946",
      });
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        item.item_id === productId
          ? {
              ...item,
              quantity: currentQty + 1,
              qty_available: realtimeQty,
            }
          : item
      )
    );
    markCartSyncDirty();
  };

  const decrementCartItemQuantity = (productId) => {
    setCart((prevCart) =>
      prevCart.flatMap((item) => {
        if (item.item_id !== productId) {
          return [item];
        }

        const currentQty = Math.max(1, Number(item.quantity) || 1);
        if (currentQty <= 1) {
          return [];
        }

        return [
          {
            ...item,
            quantity: currentQty - 1,
          },
        ];
      })
    );
    markCartSyncDirty();
  };

  const updateCartItemNote = (productId, note) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.item_id === productId ? { ...item, note } : item
      )
    );
  };

  // ── Modal ──
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const openImageModal = (product) => setSelectedImageProduct(product);
  const closeImageModal = () => setSelectedImageProduct(null);

  // ── Quantity controls (product grid) ──
  const incrementQuantity = (productId) => {
    setSelectedQuantities({
      ...selectedQuantities,
      [productId]: (selectedQuantities[productId] || 1) + 1,
    });
  };

  const decrementQuantity = (productId) => {
    const currentQuantity = selectedQuantities[productId] || 1;
    if (currentQuantity > 1) {
      setSelectedQuantities({
        ...selectedQuantities,
        [productId]: currentQuantity - 1,
      });
    }
  };

  // ── Adiciones controls (modifier groups in cart) ──
  const incrementAdiciones = async (
    productId,
    quantity_max_permitted,
    cartItem,
    addonItem
  ) => {
    const availability = await ensureAddonAvailability(cartItem, addonItem);
    if (!availability.available) {
      return;
    }

    const currentQuantity = selectedQuantities[productId] || 1;
    if (
      currentQuantity < quantity_max_permitted &&
      currentQuantity < availability.qtyAvailable
    ) {
      setSelectedQuantities({
        ...selectedQuantities,
        [productId]: currentQuantity + 1,
      });
      markCartSyncDirty();
    } else if (currentQuantity >= availability.qtyAvailable) {
      Swal.fire({
        icon: "warning",
        title: "Stock insuficiente",
        text: "No hay mas existencias disponibles para este acompañamiento.",
        confirmButtonColor: "#e63946",
      });
    }
  };

  const decrementAdiciones = (productId) => {
    const currentQuantity = selectedQuantities[productId] || 0;
    if (currentQuantity > 1) {
      setSelectedQuantities({
        ...selectedQuantities,
        [productId]: currentQuantity - 1,
      });
      markCartSyncDirty();
    }
  };

  const incrementAdicionesV2 = async (
    productId,
    quantity_max_permitted,
    cartItem,
    addonItem
  ) => {
    const availability = await ensureAddonAvailability(cartItem, addonItem);
    if (!availability.available) {
      return;
    }

    const idPrefix = productId.split("-")[0];
    const totalQuantity = Object.entries(selectedQuantitiesV2)
      .filter(([key]) => key.startsWith(idPrefix))
      .reduce((sum, [, value]) => sum + value, 0);
    const currentQuantity = selectedQuantitiesV2[productId] || 0;
    if (
      totalQuantity < quantity_max_permitted &&
      currentQuantity < availability.qtyAvailable
    ) {
      setSelectedQuantities((prev) => ({
        ...prev,
        [productId]: (prev[productId] || 0) + 1,
      }));
      setSelectedQuantitiesV2((prev) => ({
        ...prev,
        [productId]: (prev[productId] || 0) + 1,
      }));
      markCartSyncDirty();
    } else if (currentQuantity >= availability.qtyAvailable) {
      Swal.fire({
        icon: "warning",
        title: "Stock insuficiente",
        text: "No hay mas existencias disponibles para este acompañamiento.",
        confirmButtonColor: "#e63946",
      });
    }
  };

  const decrementAdicionesV2 = (productId) => {
    setSelectedQuantities((prev) => {
      const current = prev[productId] || 0;
      if (current > 0) {
        const newQty = current - 1;
        if (newQty === 0) {
          setIsChecked((pc) => ({ ...pc, [productId]: false }));
        }
        return { ...prev, [productId]: newQty };
      }
      return prev;
    });
    setSelectedQuantitiesV2((prev) => {
      const current = prev[productId] || 0;
      if (current > 0) {
        const newQty = current - 1;
        if (newQty === 0) {
          setIsChecked((pc) => ({ ...pc, [productId]: false }));
        }
        return { ...prev, [productId]: newQty };
      }
      return prev;
    });
    markCartSyncDirty();
  };

  // ── Checkbox handlers ──
  const handleCheckboxChange = async (
    event,
    quantity_max_permitted,
    uniqueId,
    cartItem,
    addonItem
  ) => {
    if (event.target.checked) {
      const availability = await ensureAddonAvailability(cartItem, addonItem);
      if (!availability.available) {
        return;
      }
    }

    const selectedCount = Object.values(isChecked).filter(Boolean).length;
    if (
      quantity_max_permitted &&
      selectedCount >= quantity_max_permitted &&
      !isChecked[uniqueId]
    ) {
      return;
    }
    setIsChecked({ ...isChecked, [uniqueId]: event.target.checked });
    setSelectedQuantities({
      ...selectedQuantities,
      [uniqueId]: event.target.checked ? 1 : 0,
    });
    setTotalSelected((prev) => prev + (event.target.checked ? 1 : -1));
    markCartSyncDirty();
  };

  const handleCheckboxChangev2 = (
    event,
    quantity_max_permitted,
    uniqueId,
    item,
    addonItem
  ) => {
    if (event.target.checked) {
      return ensureAddonAvailability(item, addonItem).then((availability) => {
        if (!availability.available) {
          return;
        }

        setIsChecked({ ...isChecked, [uniqueId]: event.target.checked });
        if (event.target.checked) {
          setSelectedQuantities((prev) => ({
            ...prev,
            [uniqueId]: (prev[uniqueId] || 0) + 1,
          }));
          setSelectedQuantitiesV2((prev) => ({
            ...prev,
            [uniqueId]: (prev[uniqueId] || 0) + 1,
          }));
        }
        setTotalSelected((prev) => prev + 1);
        markCartSyncDirty();

        const requiredGroups = cart.filter((cartItem) =>
          hasModifierGroups(cartItem) &&
          cartItem.modifier_groups.some(
            (group) =>
              group.is_required === 1 &&
              !selections[group.app_modifier_group_id]
          )
        );
        setError(requiredGroups.length > 0);
      });
    }

    setIsChecked({ ...isChecked, [uniqueId]: event.target.checked });
    if (!event.target.checked) {
      setSelectedQuantities((prev) => ({ ...prev, [uniqueId]: 0 }));
      setSelectedQuantitiesV2((prev) => ({ ...prev, [uniqueId]: 0 }));
      setIsChecked((prev) => ({ ...prev, [uniqueId]: false }));
    }
    setTotalSelected((prev) => prev - 1);
    markCartSyncDirty();

    const requiredGroups = cart.filter((cartItem) =>
      hasModifierGroups(cartItem) &&
      cartItem.modifier_groups.some(
        (group) =>
          group.is_required === 1 &&
          !selections[group.app_modifier_group_id]
      )
    );
    setError(requiredGroups.length > 0);
  };

  // ── Sync checkbox state with quantities ──
  useEffect(() => {
    for (const [productId, quantity] of Object.entries(selectedQuantities)) {
      if (quantity === 0) {
        setIsChecked((prev) => ({ ...prev, [productId]: false }));
      }
    }
  }, [selectedQuantities]);

  useEffect(() => {
    for (const [productId, quantity] of Object.entries(selectedQuantitiesV2)) {
      if (quantity === 0) {
        setIsChecked((prev) => ({ ...prev, [productId]: false }));
      }
    }
  }, [selectedQuantitiesV2]);

  // ── Accordion & Radio handlers ──
  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleRadioChange = async (item, group, addonItem) => {
    const availability = await ensureAddonAvailability(item, addonItem);
    if (!availability.available) {
      return;
    }

    setSelections((prev) => {
      const groupName = `group-${item.item_id}-${group.app_modifier_group_id}`;
      if (!prev[groupName] || prev[groupName] !== item.item_id) {
        return { ...prev, [groupName]: item.item_id };
      }
      return prev;
    });
    markCartSyncDirty();
  };

  // ── Order flow ──
  const resolveClientPhone = async () => {
    const sessionPhone = String(
      sessionData?.client?.cellphone_intl ??
        sessionData?.client?.cellphone ??
        sessionData?.client?.phone ??
        sessionData?.cellphone_intl ??
        sessionData?.cellphone ??
        sessionData?.phone ??
        ""
    )
      .replace(/[^\d]/g, "")
      .trim();

    if (sessionPhone) {
      return sessionPhone;
    }

    if (IS_DEV_MODE && DEV_WHATSAPP_PHONE) {
      console.info("[Pedido] Usando telefono configurado para desarrollo", {
        phone: DEV_WHATSAPP_PHONE,
      });
      return DEV_WHATSAPP_PHONE;
    }

    return checkPhoneclient(sessionId);
  };

  const buildWhatsAppOrderDetail = () =>
    cart.map((item) => {
      const parentId = getItemIdentity(item);
      const sub_item_list = [];

      if (Array.isArray(item.modifier_groups)) {
        item.modifier_groups.forEach((group) => {
          if (!Array.isArray(group.app_mg_items)) {
            return;
          }

          group.app_mg_items.forEach((modifierItem) => {
            const modifierId = String(
              modifierItem?.app_item_id ?? modifierItem?.item_id ?? ""
            ).trim();
            const checkboxKey = `${item.item_id}-${modifierId}`;
            const selectedQty =
              Number(selectedQuantities[checkboxKey]) ||
              Number(selectedQuantitiesV2[checkboxKey]) ||
              0;

            if (selectedQty <= 0) {
              return;
            }

            const selectedModifier =
              item.acompanantes?.find(
                (acompanante) => getItemIdentity(acompanante) === modifierId
              ) || findKnownProductById(modifierId);

            sub_item_list.push({
              app_item_id: modifierId,
              name_modifier_groups:
                selectedModifier?.item_name ||
                modifierItem?.item_name ||
                `Item ${modifierId}`,
              quantity: selectedQty,
              price: Number(selectedModifier?.price ?? modifierItem?.price ?? 0),
            });
          });
        });
      }

      Object.values(selectedRadio || {}).forEach((groupSelections) => {
        Object.keys(groupSelections || {}).forEach((uniqueId) => {
          const [productId, modifierId] = uniqueId.split("-");

          if (
            String(productId) !== String(item.item_id) ||
            !groupSelections[uniqueId]
          ) {
            return;
          }

          const selectedModifier =
            item.acompanantes?.find(
              (acompanante) => getItemIdentity(acompanante) === modifierId
            ) || findKnownProductById(modifierId);

          sub_item_list.push({
            app_item_id: modifierId,
            name_modifier_groups:
              selectedModifier?.item_name || `Item ${modifierId}`,
            quantity: 1,
            price: Number(selectedModifier?.price ?? 0),
          });
        });
      });

      if (Array.isArray(item.subproducts)) {
        item.subproducts.forEach((subproduct) => {
          const subproductId = getItemIdentity(subproduct);
          const subKey = `${item.item_id}-sub-${subproductId}`;
          const subQty = Number(selectedQuantities[subKey]) || 0;

          if (subQty <= 0) {
            return;
          }

          sub_item_list.push({
            app_item_id: subproductId,
            name_modifier_groups:
              subproduct?.item_name || `Subproducto ${subproductId}`,
            quantity: subQty,
            price: Number(subproduct?.price ?? 0),
          });
        });
      }

      return {
        app_item_id: parentId,
        quantity: Math.max(1, Number(item.quantity) || 1),
        sub_item_list,
      };
    });

  const buildCartProductsPayload = () => {
    const payloadMap = new Map();

    const appendProduct = (item, quantity, notes = "", selectionKeys = []) => {
      const barcode = getBarcodeIdentity(item);
      const normalizedQuantity = Math.max(0, Number(quantity) || 0);

      if (!barcode || normalizedQuantity <= 0) {
        if (normalizedQuantity > 0) {
          console.warn("[Pedido] Producto omitido del payload por falta de barcode", {
            item,
            quantity: normalizedQuantity,
          });
        }
        return;
      }

      const existing = payloadMap.get(barcode);
      if (existing) {
        existing.quantity += normalizedQuantity;
        existing.selectionKeys = Array.from(
          new Set([...(existing.selectionKeys || []), ...selectionKeys])
        );
        return;
      }

      payloadMap.set(barcode, {
        barcode,
        quantity: normalizedQuantity,
        notes,
        name: getItemDisplayName(item),
        selectionKeys,
      });
    };

    cart.forEach((item) => {
      appendProduct(item, item.quantity, item.note || "");

      if (Array.isArray(item.modifier_groups)) {
        item.modifier_groups.forEach((group) => {
          if (!Array.isArray(group.app_mg_items)) {
            return;
          }

          group.app_mg_items.forEach((modifierItem) => {
            const selectedModifier =
              item.acompanantes?.find(
                (acompanante) =>
                  getItemIdentity(acompanante) === getItemIdentity(modifierItem)
              ) || findKnownProductById(getItemIdentity(modifierItem));
            const checkboxKey = `${item.item_id}-${getItemIdentity(modifierItem)}`;
            const selectedQty =
              Number(selectedQuantities[checkboxKey]) ||
              Number(selectedQuantitiesV2[checkboxKey]) ||
              0;

            if (selectedQty > 0) {
              appendProduct(
                selectedModifier || modifierItem,
                selectedQty,
                "",
                [checkboxKey]
              );
            }
          });
        });
      }

      Object.values(selectedRadio || {}).forEach((groupSelections) => {
        Object.keys(groupSelections || {}).forEach((uniqueId) => {
          const [productId, modifierId] = uniqueId.split("-");
          if (
            String(productId) !== String(item.item_id) ||
            !groupSelections[uniqueId]
          ) {
            return;
          }

          const selectedModifier =
            item.acompanantes?.find(
              (acompanante) => getItemIdentity(acompanante) === modifierId
            ) || findKnownProductById(modifierId);
          appendProduct(selectedModifier, 1, "", [uniqueId]);
        });
      });

      if (Array.isArray(item.subproducts)) {
        item.subproducts.forEach((subproduct) => {
          const subKey = `${item.item_id}-sub-${getItemIdentity(subproduct)}`;
          const subQty = Number(selectedQuantities[subKey]) || 0;

          if (subQty > 0) {
            appendProduct(subproduct, subQty, "", [subKey]);
          }
        });
      }
    });

    return Array.from(payloadMap.values());
  };

  const validateCartProductsAvailability = async (productsPayload) => {
    const availabilityChecks = await Promise.all(
      productsPayload.map(async (product) => {
        const availability = await getProductAvailability(product.barcode, sedeId);
        const availableQty = normalizeAvailableQty(availability?.qty_available);

        return {
          ...product,
          availableQty,
        };
      })
    );

    return availabilityChecks.filter(
      (product) => product.quantity > product.availableQty
    );
  };

  const syncCartState = useCallback(async (options = {}) => {
    const { productsPayloadWithMeta, silent = false } = options;

    if (!sessionId) {
      return { ok: false, reason: "missing_session" };
    }

    const resolvedProductsPayloadWithMeta =
      productsPayloadWithMeta || buildCartProductsPayload();

    const invalidProducts = resolvedProductsPayloadWithMeta.filter(
      (item) => !item.barcode || item.quantity <= 0
    );

    if (invalidProducts.length > 0) {
      const errorMessage =
        "Uno o varios productos no tienen barcode o cantidad valida para actualizar el pedido.";

      console.warn("[Pedido] Sincronizacion omitida por productos incompletos", {
        invalidProducts,
      });

      if (!silent) {
        setCartStockErrorMessage(errorMessage);
      }

      return {
        ok: false,
        reason: "invalid_products",
        errorMessage,
      };
    }

    const productsPayload = resolvedProductsPayloadWithMeta.map(
      ({ name, ...product }) => product
    );

    const cartUpdateResponse = await updateCartProducts(sessionId, productsPayload);

    if (!silent) {
      setCartStockErrorMessage("");
    }

    return {
      ok: true,
      productsPayload,
      cartUpdateResponse,
    };
  }, [
    cart,
    searchResults,
    selectedQuantities,
    selectedQuantitiesV2,
    selectedRadio,
    products,
    sessionId,
    total,
  ]);

  useEffect(() => {
    if (!hasUserTouchedCart || !sessionId || isSubmittingOrder) {
      return;
    }

    if (cartSyncTimeoutRef.current) {
      clearTimeout(cartSyncTimeoutRef.current);
    }

    const requestId = ++cartSyncRequestIdRef.current;

    cartSyncTimeoutRef.current = setTimeout(async () => {
      try {
        setIsAutoSyncingCart(true);
        const syncResult = await syncCartState({ silent: true });

        if (!syncResult.ok) {
          console.warn("[Pedido] Autosincronizacion omitida", syncResult);
        } else {
          console.info("[Pedido] Carrito autosincronizado correctamente", {
            requestId,
            products: syncResult.productsPayload.length,
          });
        }
      } catch (error) {
        console.error("[Pedido] Error en autosincronizacion del carrito:", error);
      } finally {
        if (cartSyncRequestIdRef.current === requestId) {
          setIsAutoSyncingCart(false);
        }
      }
    }, 700);

    return () => {
      if (cartSyncTimeoutRef.current) {
        clearTimeout(cartSyncTimeoutRef.current);
      }
    };
  }, [cartSyncVersion, hasUserTouchedCart, isSubmittingOrder, sessionId, syncCartState]);

  let isQuantityValid = true;

  const MessageValorTotal = () =>
    minimumOrderValue
      ? `El valor minimo del pedido debe ser mayor o igual a ${formatCOP(
        minimumOrderValue
      )}.`
      : "El valor del pedido no cumple con el minimo requerido.";
  /*
      title: "Valor mínimo no alcanzado",
      text: minimumOrderValue
        ? `El valor del pedido debe ser mayor o igual a ${minimumOrderText}`
        : "El valor del pedido no cumple con el minimo requerido.",
      confirmButtonText: "Ok",
      confirmButtonColor: "#e63946",
    });
  */

  const MessageAlertUpdatingOrder = () => {
    Swal.fire({
      icon: "success",
      title: "Actualizando pedido",
      html: '<div class="progress-bar-container"><div class="progress-bar"></div></div>',
      showConfirmButton: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
  };

  const InitOrderTemporary = async () => {
    if (isSubmittingOrder || hasConfirmedWhatsAppOrder) {
      console.info("[Pedido] Envio ignorado porque ya hay un proceso en curso");
      return;
    }

    if (cartSyncTimeoutRef.current) {
      clearTimeout(cartSyncTimeoutRef.current);
    }

    setIsSubmittingOrder(true);
    setCartStockErrorMessage("");
    console.info("[Pedido] Iniciando flujo de pedido WhatsApp", {
      sessionId,
      total,
      cartItems: cart.length,
    });

    if (minimumOrderValue && total < minimumOrderValue) {
      setMinimumOrderError(true);
      setIsSubmittingOrder(false);
      return;
    }

    setMinimumOrderError(false);

    // Validate required modifier groups (only for products that have them)
    const requiredGroups = cart.filter(
      (item) =>
        hasModifierGroups(item) &&
        item.modifier_groups.some(
          (group) =>
            group.is_required === 1 &&
            !selections[`group-${item.item_id}-${group.app_modifier_group_id}`]
        )
    );
    if (requiredGroups.length > 0) {
      setError(true);
      setIsSubmittingOrder(false);
      return;
    }

    // Validate quantity-based modifier groups (is_required === 3)
    cart.forEach((item) => {
      if (!hasModifierGroups(item)) return;
      item.modifier_groups.forEach((group) => {
        if (group.is_required === 3) {
          const idPrefix = `${item.item_id}-`;
          const selectedQtys = Object.entries(selectedQuantitiesV2)
            .filter(([key]) => key.startsWith(idPrefix))
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
          const totalQty = Object.values(selectedQtys).reduce(
            (sum, qty) => sum + qty,
            0
          );
          if (totalQty !== group.quantity_max_permitted) {
            isQuantityValid = false;
          }
        }
      });
    });

    if (!isQuantityValid) {
      setError(true);
      setIsSubmittingOrder(false);
      return;
    }

    MessageAlertUpdatingOrder();

    try {
      let productsPayloadWithMeta = buildCartProductsPayload();
      let selectedProducts = cart.map((item) => ({
        app_item_id: getItemIdentity(item),
        item_id: item.item_id,
        name: item.item_name,
        price: Number(getDiscountedPrice(item)),
        original_price: Number(item.price) || 0,
        quantity: Math.max(1, Number(item.quantity) || 1),
        measure_unit: item.measure_unit || "",
        note: item.note || "",
      }));
      const itemPedidosIds = buildWhatsAppOrderDetail();

      console.info("[Pedido] Productos listos para procesar", {
        productsPayload: productsPayloadWithMeta,
        selectedProducts,
        itemPedidosIds,
      });
      console.info(
        "[Pedido] Payload final para /cart/{token}",
        productsPayloadWithMeta
      );

      const invalidProducts = productsPayloadWithMeta.filter(
        (item) => !item.barcode || item.quantity <= 0
      );

      if (invalidProducts.length > 0) {
        setIsSubmittingOrder(false);
        setCartStockErrorMessage(
          "Uno o varios productos no tienen barcode o cantidad valida para actualizar el pedido."
        );
        Swal.close();
        return;
        Swal.fire({
          icon: "error",
          title: "Productos incompletos",
          text: "Uno o varios productos no tienen barcode o cantidad válida para actualizar el pedido.",
          confirmButtonColor: "#e63946",
        });
        return;
      }

      const unavailableProducts = await validateCartProductsAvailability(
        productsPayloadWithMeta
      );

      if (unavailableProducts.length > 0) {
        // Separate main products (no selectionKeys) from addons (have selectionKeys)
        const mainUnavailable = unavailableProducts.filter(
          (p) => !p.selectionKeys?.length
        );
        const addonUnavailable = unavailableProducts.filter(
          (p) => p.selectionKeys?.length > 0
        );

        const toReduce = mainUnavailable.filter((p) => p.availableQty > 0);
        const toRemove = mainUnavailable.filter((p) => p.availableQty === 0);

        // Clear unavailable addon selections (existing behavior preserved)
        const unavailableSelectionKeys = addonUnavailable.flatMap(
          (item) => item.selectionKeys || []
        );
        if (unavailableSelectionKeys.length > 0) {
          setUnavailableAddons((prev) => {
            const next = { ...prev };
            unavailableSelectionKeys.forEach((key) => { next[key] = true; });
            return next;
          });
          setIsChecked((prev) => {
            const next = { ...prev };
            unavailableSelectionKeys.forEach((key) => { next[key] = false; });
            return next;
          });
          setSelectedQuantities((prev) => {
            const next = { ...prev };
            unavailableSelectionKeys.forEach((key) => { next[key] = 0; });
            return next;
          });
          setSelectedQuantitiesV2((prev) => {
            const next = { ...prev };
            unavailableSelectionKeys.forEach((key) => { next[key] = 0; });
            return next;
          });
          setSelectedRadio((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((groupName) => {
              const groupSelections = { ...(next[groupName] || {}) };
              unavailableSelectionKeys.forEach((key) => { delete groupSelections[key]; });
              next[groupName] = groupSelections;
            });
            return next;
          });
        }

        // Auto-adjust main products
        const originalQtyMap = Object.fromEntries(
          productsPayloadWithMeta.map((p) => [p.barcode, p.quantity])
        );

        // Collect IDs for visual highlight (read cart before state update)
        const adjustedItemIds = new Set(
          cart
            .filter((item) => toReduce.some((p) => p.barcode === item.item_barcode))
            .map((item) => item.item_id)
        );

        if (toReduce.length > 0 || toRemove.length > 0) {
          setCart((prevCart) =>
            prevCart
              .map((item) => {
                const reduced = toReduce.find((p) => p.barcode === item.item_barcode);
                return reduced ? { ...item, quantity: reduced.availableQty } : item;
              })
              .filter((item) => !toRemove.some((p) => p.barcode === item.item_barcode))
          );
        }

        if (adjustedItemIds.size > 0) {
          setAdjustedCartItems(new Set(adjustedItemIds));
          setTimeout(() => setAdjustedCartItems(new Set()), 4000);
        }

        // Build adjusted payload without waiting for React state flush
        const adjustedPayload = productsPayloadWithMeta
          .map((item) => {
            const reduced = toReduce.find((p) => p.barcode === item.barcode);
            return reduced ? { ...item, quantity: reduced.availableQty } : item;
          })
          .filter((item) => !toRemove.some((p) => p.barcode === item.barcode));

        const adjustedSelectedProducts = cart
          .filter((item) => !toRemove.some((p) => p.barcode === item.item_barcode))
          .map((item) => {
            const reduced = toReduce.find((p) => p.barcode === item.item_barcode);
            const quantity = reduced
              ? reduced.availableQty
              : Math.max(1, Number(item.quantity) || 1);
            return {
              app_item_id: getItemIdentity(item),
              item_id: item.item_id,
              name: item.item_name,
              price: Number(getDiscountedPrice(item)),
              original_price: Number(item.price) || 0,
              quantity,
              measure_unit: item.measure_unit || "",
              note: item.note || "",
            };
          });

        // Build notification HTML
        const buildLines = (items, detail) =>
          items
            .map((p) => `<li><b>${p.name || p.barcode}</b>${detail(p)}</li>`)
            .join("");

        const notificationHtml = [
          toRemove.length > 0 &&
            `<p style="margin:0 0 6px 0"><b>Sin stock — eliminados del carrito:</b></p>
             <ul style="margin:0 0 10px 0;padding-left:18px;text-align:left">
               ${buildLines(toRemove, () => "")}
             </ul>`,
          toReduce.length > 0 &&
            `<p style="margin:0 0 6px 0"><b>Cantidad ajustada al stock disponible:</b></p>
             <ul style="margin:0;padding-left:18px;text-align:left">
               ${buildLines(
                 toReduce,
                 (p) =>
                   ` — <span style="text-decoration:line-through;color:#9ca3af">${originalQtyMap[p.barcode] ?? "?"}</span> → <b>${p.availableQty}</b> ud.`
               )}
             </ul>`,
          addonUnavailable.length > 0 &&
            `<p style="margin:10px 0 0 0;font-size:12px;color:#6b7280">
               Algunos acompañamientos sin stock también fueron desmarcados.
             </p>`,
        ]
          .filter(Boolean)
          .join("");

        Swal.close();
        setIsSubmittingOrder(false);

        if (adjustedPayload.length === 0) {
          await Swal.fire({
            icon: "warning",
            title: "Carrito vacío",
            text: "Todos los productos del carrito están sin stock en este momento.",
            confirmButtonColor: "#e63946",
            allowOutsideClick: false,
          });
          return;
        }

        const { isConfirmed } = await Swal.fire({
          icon: "info",
          title: "Ajuste por disponibilidad de stock",
          html: notificationHtml +
            `<p style="margin:14px 0 0 0;font-size:13px;color:#6b7280">
               ¿Deseas continuar con el pedido ajustado, o prefieres editar el carrito primero?
             </p>`,
          confirmButtonText: "Sí, continuar con el pedido",
          confirmButtonColor: "#25d366",
          cancelButtonText: "Editar carrito",
          cancelButtonColor: "#6b7280",
          showCancelButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        if (!isConfirmed) {
          // User chose to edit — cart already has adjustments applied, return to it
          return;
        }

        // User confirmed — continue checkout with adjusted data
        setIsSubmittingOrder(true);
        productsPayloadWithMeta = adjustedPayload;
        selectedProducts = adjustedSelectedProducts;
      }

      const syncResult = await syncCartState({ productsPayloadWithMeta });

      if (!syncResult.ok) {
        setIsSubmittingOrder(false);
        Swal.close();
        return;
      }

      console.info("[Pedido] Carrito actualizado correctamente", {
        cartUpdateResponse: syncResult.cartUpdateResponse,
      });

      const phone = await resolveClientPhone();
      console.info("[Pedido] Telefono resuelto para WhatsApp", { phone });

      if (!phone) {
        throw new Error(
          "No se encontro un telefono valido para enviar a WhatsApp."
        );
      }

      const whatsappResponse = await ExecApiDetailOrderWhatsap(
        sessionId,
        selectedProducts,
        phone,
        itemPedidosIds,
        total
      );

      if (!whatsappResponse?.ok) {
        throw (
          whatsappResponse?.error ||
          new Error("No fue posible enviar el detalle del pedido a WhatsApp.")
        );
      }

      setHasConfirmedWhatsAppOrder(true);
      setIsSubmittingOrder(false);
      closeModal();
      if (REDIRECT_WHATSAPP) {
        window.location.href = REDIRECT_WHATSAPP;
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Pedido actualizado",
        text: "Los productos del pedido se actualizaron correctamente.",
        confirmButtonColor: "#e63946",
      });
    } catch (err) {
      setIsSubmittingOrder(false);
      console.error("Error al procesar la solicitud:", err);
      Swal.fire({
        icon: "error",
        title: "Error al actualizar el pedido",
        text: "Ocurrió un problema al guardar los productos seleccionados.",
        confirmButtonColor: "#e63946",
      });
    }
  };

  // ── Generate pagination numbers ──
  useEffect(() => {
    if (Object.values(unavailableAddons).every((value) => value !== true)) {
      setAddonStockMessage("");
    }
  }, [unavailableAddons]);

  useEffect(() => {
    setHasConfirmedWhatsAppOrder(false);
  }, [cart, total]);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, apiPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  // ═══════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════
  return (
    <React.Fragment>
      {/* ===== Category Pills Bar ===== */}
      <div className="mobile-category-trigger">
        <button
          type="button"
          className="mobile-category-button"
          onClick={() => setIsCategoryMenuOpen(true)}
          aria-label="Abrir categorías"
        >
          <span className="mobile-category-button-label">Categorías</span>
          <span className="mobile-category-button-value">
            {selectedCategoryLabel}
          </span>
        </button>
      </div>

      <div className="category-bar-wrapper">
        <button
          className={`category-scroll-btn left ${!canScrollLeft ? "hidden" : ""}`}
          onClick={() => scrollCategories("left")}
          aria-label="Scroll categorías izquierda"
        >
          ‹
        </button>
        <div className="category-bar" ref={categoryBarRef}>
            {categories.map((category) => (
              <button
              type="button"
              data-category-id={getCategoryIdentity(category)}
              key={getCategoryIdentity(category)}
              className={`category-pill ${selectedCategory === getCategoryIdentity(category) ? "active" : ""
                }`}
              onClick={() => handleCategoryChange(getCategoryIdentity(category))}
            >
              {category.name}
            </button>
          ))}
        </div>
        <button
          className={`category-scroll-btn right ${!canScrollRight ? "hidden" : ""}`}
          onClick={() => scrollCategories("right")}
          aria-label="Scroll categorías derecha"
        >
          ›
        </button>
      </div>

      {isCategoryMenuOpen && (
        <div
          className="mobile-category-sheet-overlay"
          onClick={() => setIsCategoryMenuOpen(false)}
        >
          <div
            className="mobile-category-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-category-sheet-header">
              <div>
                <p className="mobile-category-sheet-eyebrow">Explora el catálogo</p>
                <h3>Categorías</h3>
              </div>
              <button
                type="button"
                className="mobile-category-sheet-close"
                onClick={() => setIsCategoryMenuOpen(false)}
                aria-label="Cerrar categorías"
              >
                ×
              </button>
            </div>

            <div className="mobile-category-sheet-list">
              {categories.map((category) => {
                const categoryId = getCategoryIdentity(category);
                const isActive = selectedCategory === categoryId;

                return (
                  <button
                    key={`mobile-${categoryId}`}
                    type="button"
                    className={`mobile-category-item ${isActive ? "active" : ""}`}
                    onClick={() => handleCategoryChange(categoryId)}
                  >
                    <span>{category.name}</span>
                    {isActive && <span className="mobile-category-item-check">Actual</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== Floating Cart Button ===== */}
      <div className="sidebar">
        <div className={`ex-fdlist ${isAddingToCart ? "cart-fly" : ""}`}>
          <div
            className={`exfd-shopping-cart ${shouldBlink ? "blink" : ""}`}
            onClick={openModal}
          >
            <img decoding="async" src="/carritokokoriko.svg" alt="Carrito" />
            <span className="exfd-cart-num">{cart.length}</span>
            <div className="exfd-cart-meta">
              <span className="exfd-cart-label">
                <FontAwesomeIcon icon={faWhatsapp} className="whatsapp-inline-icon" />
                Pedido WhatsApp
              </span>
              <span className="exfd-cart-total">{formattedTotal}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="catalog-toolbar">
      {/* ===== Search Bar ===== */}
      <div className={`search-section ${isSearchMode ? "search-active" : ""}`}>
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          placeholder="Buscar productos... (ej: agua, pan, arroz)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {isSearching && <div className="search-spinner"></div>}
        {searchTerm && (
          <button
            className="search-clear-btn"
            onClick={clearSearch}
            aria-label="Limpiar búsqueda"
          >
            ✕
          </button>
        )}
      </div>

      </div>

      {/* ===== Product Grid ===== */}
      <div className="product-menu">
        {/* Pagination / Search Info */}
        {!isLoading && (
          <div className="pagination-info">
            {isSearchMode ? (
              <span>
                {isSearching
                  ? "Buscando..."
                  : `${searchResults.length} resultado${searchResults.length !== 1 ? "s" : ""} para "${searchTerm}"`}
              </span>
            ) : totalProducts > 0 ? (
              <span>
                Mostrando {displayProducts.length} de {totalProducts} productos
              </span>
            ) : null}
          </div>
        )}

        <div className="products-section">
          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Cargando productos...</p>
            </div>
          ) : displayProducts.length === 0 ? (
            <div className="loading-container">
              <p>
                {searchTerm
                  ? "No se encontraron productos con ese criterio de búsqueda."
                  : "No hay productos disponibles en esta categoría."}
              </p>
            </div>
          ) : (
            displayProducts.map((product, index) => {
              const discountedPrice = getDiscountedPrice(product);
              const hasDiscount = hasActiveDiscount(product);

              return (
                <div
                  key={`${getProductIdentity(product)}-${index}`}
                  className="product-item"
                >
                  {/* Discount Badge */}
                  {hasDiscount && (
                    <div className="discount-badge">
                      -{product.discount}%
                    </div>
                  )}

                  {/* Product Image */}
                  <button
                    type="button"
                    className="product-image-wrapper"
                    onClick={() => product.img_src && openImageModal(product)}
                    disabled={!product.img_src}
                    aria-label={
                      product.img_src
                        ? `Ver imagen ampliada de ${product.item_name}`
                        : `Imagen no disponible para ${product.item_name}`
                    }
                  >
                    {product.img_src ? (
                      <img
                        src={product.img_src}
                        alt={product.item_name}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('placeholder-active');
                        }}
                      />
                    ) : (
                      <div className="product-image-placeholder">
                        <span className="placeholder-icon">
                          {(product.brand || product.item_name || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="placeholder-category">{product.category_name || ''}</span>
                      </div>
                    )}
                    {product.img_src && (
                      <span className="product-image-hint">Toca para ampliar</span>
                    )}
                  </button>

                  {/* Product Info */}
                  <div className="product-info">
                    <h3>{product.item_name}</h3>
                    <p className="product-brand">{product.brand}</p>
                    {product.measure_unit && (
                      <p className="product-measure">
                        {product.measure_unit}
                      </p>
                    )}
                    <div className="product-price-section">
                      {hasDiscount ? (
                        <>
                          <p className="product-price-original">
                            {formatCOP(product.price)}
                          </p>
                          <p className="product-price product-price-discount">
                            {formatCOP(discountedPrice)}
                          </p>
                        </>
                      ) : (
                        <p className="product-price">
                          {formatCOP(product.price)}
                        </p>
                      )}
                    </div>
                    {false && product.qty_available > 0 &&
                      product.qty_available <= 5 && (
                        <p className="product-stock-low">
                          ¡Quedan {product.qty_available}!
                        </p>
                      )}
                  </div>

                  {/* Product Actions */}
                    <div className="product-actions">
                      <div className="quantity-stepper">
                        <button
                          onClick={() => decrementQuantity(product.item_id)}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={selectedQuantities[product.item_id] || 1}
                          onChange={(e) =>
                            setSelectedQuantities({
                              ...selectedQuantities,
                              [product.item_id]: Math.max(
                                1,
                                parseInt(e.target.value, 10) || 1
                              ),
                            })
                          }
                        />
                        <button
                          onClick={() => incrementQuantity(product.item_id)}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="add-to-cart"
                        onClick={() =>
                          addToCart(
                            product.item_id,
                            selectedQuantities[product.item_id] || 1
                          )
                        }
                      >
                        <FontAwesomeIcon icon={faWhatsapp} className="whatsapp-inline-icon" />
                        Añadir
                      </button>
                    </div>
                </div>
              );
            })
          )}
        </div>

        {/* ===== Cart Modal ===== */}
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          style={customStyles}
          contentLabel="Carrito de compras"
        >
          <button onClick={closeModal} className="button-volver">
            ← Volver al menú
          </button>

          <div className="cart-items-scroll">
          {cart.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "var(--color-text-light)",
              }}
            >
              <p style={{ fontSize: "var(--font-size-lg)", fontWeight: 500 }}>
                Tu carrito está vacío
              </p>
              <p style={{ fontSize: "var(--font-size-sm)", marginTop: "8px" }}>
                Añade productos desde el catálogo
              </p>
            </div>
          )}

          {cart.map((item, itemIndex) => {
            const itemDiscountedPrice = getDiscountedPrice(item);

            return (
              <div
                key={item.item_id}
                className={`cart-item${adjustedCartItems.has(item.item_id) ? " cart-item--adjusted" : ""}`}
              >
                <h3>{item.item_name}</h3>
                <p>Precio: {formatCOP(itemDiscountedPrice)}</p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "12px",
                  }}
                >
                  <span style={{ fontWeight: 500 }}>Cantidad:</span>
                  <button
                    className="button-size"
                    onClick={() => decrementCartItemQuantity(item.item_id)}
                  >
                    -
                  </button>
                  <span
                    style={{
                      fontWeight: 600,
                      minWidth: "20px",
                      display: "inline-block",
                      textAlign: "center",
                    }}
                  >
                    {item.quantity}
                  </span>
                  <button
                    className="button-size"
                    onClick={() => incrementCartItemQuantity(item.item_id)}
                  >
                    +
                  </button>
                </div>

                <div style={{ marginBottom: "12px" }}>
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedNoteItemId((prev) =>
                        prev === item.item_id ? null : item.item_id
                      )
                    }
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--color-text)",
                      fontWeight: 500,
                      fontSize: "var(--font-size-sm)",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    {expandedNoteItemId === item.item_id
                      ? "Agregar observación −"
                      : "Agregar observación +"}
                  </button>

                  {expandedNoteItemId === item.item_id && (
                    <textarea
                      autoFocus
                      value={item.note || ""}
                      placeholder="Agrega una nota para este producto (ej. sin cebolla, extra salsa...)"
                      onChange={(e) =>
                        updateCartItemNote(item.item_id, e.target.value)
                      }
                      onBlur={() => markCartSyncDirty()}
                      rows={2}
                      style={{
                        display: "block",
                        width: "100%",
                        marginTop: "6px",
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid var(--color-border, #ccc)",
                        fontFamily: "var(--font-family)",
                        fontSize: "var(--font-size-sm)",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  )}
                </div>

                {/* Modifier groups accordion (only if product has them) */}
                {hasModifierGroups(item) &&
                  item.modifier_groups.map((group, groupIndex) => {
                    const panel = `panel${itemIndex}-${groupIndex}`;
                    return (
                      <Accordion
                        expanded={
                          expanded === panel ||
                          group.is_required === 1 ||
                          group.is_required === 3
                        }
                        onChange={handleChange(panel)}
                        key={group.app_modifier_group_id}
                        className="accordion"
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls={`panel${itemIndex}-${groupIndex}bh-content`}
                          id={`panel${itemIndex}-${groupIndex}bh-header`}
                          className="accordion-summary"
                        >
                          <Typography
                            sx={{
                              width: "100%",
                              flexShrink: 0,
                              fontWeight: 600,
                              fontFamily: "var(--font-family)",
                              fontSize: "var(--font-size-sm)",
                            }}
                          >
                            {group.modifier_group_name}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className="accordion-details">
                          {(() => {
                            if (group.is_required === 3) {
                              const idPrefix = `${item.item_id}-`;
                              const selectedQtysForItem = Object.entries(
                                selectedQuantities
                              )
                                .filter(([key]) => key.startsWith(idPrefix))
                                .reduce(
                                  (obj, [key, value]) => ({
                                    ...obj,
                                    [key]: value,
                                  }),
                                  {}
                                );
                              const totalQty = Object.values(
                                selectedQtysForItem
                              ).reduce((sum, q) => sum + q, 0);
                              isQuantityValid =
                                totalQty === group.quantity_max_permitted;
                              if (!isQuantityValid) {
                                return (
                                  <div
                                    style={{
                                      color: "var(--color-danger)",
                                      fontSize: "var(--font-size-sm)",
                                      fontWeight: 500,
                                      padding: "8px 12px",
                                      background: "rgba(239, 68, 68, 0.08)",
                                      borderRadius: "8px",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    Debes seleccionar la cantidad requerida de
                                    acompañamientos (
                                    {group.quantity_max_permitted}).
                                  </div>
                                );
                              }
                            }
                          })()}
                          {Array.isArray(group.app_mg_items) &&
                            group.app_mg_items.map((bebida) => {
                              const addonSelectionKey = getAddonSelectionKey(
                                item,
                                bebida
                              );
                              const isAddonUnavailable =
                                unavailableAddons[addonSelectionKey] === true;
                              const acompanante = item.acompanantes
                                ? item.acompanantes.find(
                                  (a) =>
                                    a.app_item_id === bebida.app_item_id
                                )
                                : null;
                              const price = acompanante
                                ? acompanante.price
                                : 0;
                              return (
                                <div
                                  key={bebida.app_item_id}
                                  className="cart-opciones"
                                >
                                  {(() => {
                                    switch (group.is_required) {
                                      case 1:
                                        return (
                                          <>
                                            {error && (
                                              <div
                                                style={{
                                                  color: "var(--color-danger)",
                                                  fontSize:
                                                    "var(--font-size-xs)",
                                                  fontWeight: 500,
                                                }}
                                              >
                                                Selecciona una opción
                                              </div>
                                            )}
                                            <input
                                              className="radio-button"
                                              type="radio"
                                              id={`${item.item_id}-${bebida.app_item_id}`}
                                              name={`group-${item.item_id}-${group.app_modifier_group_id}`}
                                              value={bebida.app_item_id}
                                              disabled={isAddonUnavailable}
                                              onChange={(e) => {
                                                handleRadioChange(item, group, bebida);
                                                setSelectedRadio(
                                                  (prevSelectedRadio) => {
                                                    const groupName = `group-${item.item_id}-${group.app_modifier_group_id}`;
                                                    const updatedGroupSelections =
                                                      {};
                                                    updatedGroupSelections[
                                                      `${item.item_id}-${e.target.value}`
                                                    ] = true;
                                                    return {
                                                      ...prevSelectedRadio,
                                                      [groupName]:
                                                        updatedGroupSelections,
                                                    };
                                                  }
                                                );
                                              }}
                                            />
                                          </>
                                        );
                                      case 2:
                                        return (
                                          <>
                                            <input
                                              type="checkbox"
                                              id={`${item.item_id}-${bebida.app_item_id}`}
                                              name={
                                                group.app_modifier_group_id
                                              }
                                              value={bebida.app_item_id}
                                              checked={
                                                isChecked[
                                                  `${item.item_id}-${bebida.app_item_id}`
                                                ] || false
                                              }
                                              disabled={isAddonUnavailable}
                                              onChange={(e) => {
                                                handleCheckboxChange(
                                                  e,
                                                  group.quantity_max_permitted,
                                                  `${item.item_id}-${bebida.app_item_id}`,
                                                  item,
                                                  bebida
                                                );
                                              }}
                                            />
                                            {isChecked[
                                              `${item.item_id}-${bebida.app_item_id}`
                                            ] && (
                                                <div>
                                                  <button
                                                    className="button-size"
                                                    onClick={() =>
                                                      decrementAdiciones(
                                                        `${item.item_id}-${bebida.app_item_id}`
                                                      )
                                                    }
                                                  >
                                                    −
                                                  </button>
                                                  <span
                                                    style={{
                                                      fontWeight: 600,
                                                      minWidth: "20px",
                                                      display: "inline-block",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {selectedQuantities[
                                                      `${item.item_id}-${bebida.app_item_id}`
                                                    ] || 1}
                                                  </span>
                                                  <button
                                                    className="button-size"
                                                    onClick={() =>
                                                      incrementAdiciones(
                                                        `${item.item_id}-${bebida.app_item_id}`,
                                                        group.quantity_max_permitted,
                                                        item,
                                                        bebida
                                                      )
                                                    }
                                                  >
                                                    +
                                                  </button>
                                                </div>
                                              )}
                                          </>
                                        );
                                      case 3:
                                        const uniqueId = `${item.item_id}-${bebida.app_item_id}`;
                                        const idPrefix3 = `${item.item_id}-`;
                                        const totalQty3 = Object.entries(
                                          selectedQuantitiesV2
                                        )
                                          .filter(([key]) =>
                                            key.startsWith(idPrefix3)
                                          )
                                          .reduce(
                                            (sum, [, val]) => sum + val,
                                            0
                                          );
                                        return (
                                          <>
                                            <input
                                              type="checkbox"
                                              id={uniqueId}
                                              name={
                                                group.app_modifier_group_id
                                              }
                                              value={bebida.app_item_id}
                                              onChange={(event) => {
                                                handleCheckboxChangev2(
                                                  event,
                                                  group.quantity_max_permitted,
                                                  uniqueId,
                                                  item,
                                                  bebida
                                                );
                                              }}
                                              checked={
                                                isChecked[uniqueId] || false
                                              }
                                              disabled={
                                                isAddonUnavailable ||
                                                (totalQty3 >=
                                                  group.quantity_max_permitted &&
                                                  !isChecked[uniqueId])
                                              }
                                            />
                                            {isChecked[uniqueId] && (
                                              <div>
                                                <button
                                                  className="button-size"
                                                  onClick={() =>
                                                    decrementAdicionesV2(
                                                      uniqueId
                                                    )
                                                  }
                                                >
                                                  −
                                                </button>
                                                <span
                                                  style={{
                                                    fontWeight: 600,
                                                    minWidth: "20px",
                                                    display: "inline-block",
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {selectedQuantities[
                                                    uniqueId
                                                  ] || 1}
                                                </span>
                                                <button
                                                  className="button-size"
                                                  onClick={() =>
                                                    incrementAdicionesV2(
                                                      uniqueId,
                                                      group.quantity_max_permitted,
                                                      item,
                                                      bebida
                                                    )
                                                  }
                                                >
                                                  +
                                                </button>
                                              </div>
                                            )}
                                          </>
                                        );
                                      default:
                                        return null;
                                    }
                                  })()}
                                  <label htmlFor={bebida.app_item_id}>
                                    {acompanante
                                      ? acompanante.item_name
                                      : bebida.app_item_id}
                                  </label>
                                  {isAddonUnavailable && (
                                    <span
                                      style={{
                                        color: "var(--color-danger)",
                                        fontSize: "var(--font-size-xs)",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      Sin existencias
                                    </span>
                                  )}
                                  <p>{formatCOP(price)}</p>
                                </div>
                              );
                            })}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}

                {/* Subproducts accordion (new API format for acompañamientos) */}
                {hasSubproducts(item) && (
                  <Accordion
                    expanded={expanded === `subpanel-${itemIndex}` || true}
                    onChange={handleChange(`subpanel-${itemIndex}`)}
                    className="accordion"
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className="accordion-summary"
                    >
                      <Typography
                        sx={{
                          width: "100%",
                          flexShrink: 0,
                          fontWeight: 600,
                          fontFamily: "var(--font-family)",
                          fontSize: "var(--font-size-sm)",
                        }}
                      >
                        Acompañamientos ({item.subproducts.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className="accordion-details">
                      {item.subproducts.map((sub) => {
                        const subKey = `${item.item_id}-sub-${sub.item_id}`;
                        const subChecked = isChecked[subKey] || false;
                        const subQty = selectedQuantities[subKey] || 0;

                        return (
                          <div key={sub.item_id} className="cart-opciones">
                            <input
                              type="checkbox"
                              id={subKey}
                              checked={subChecked}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setIsChecked((prev) => ({
                                  ...prev,
                                  [subKey]: checked,
                                }));
                                setSelectedQuantities((prev) => ({
                                  ...prev,
                                  [subKey]: checked ? 1 : 0,
                                }));
                                markCartSyncDirty();
                              }}
                            />
                            {subChecked && (
                              <div>
                                <button
                                  className="button-size"
                                  onClick={() => {
                                    if (subQty > 1) {
                                      setSelectedQuantities((prev) => ({
                                        ...prev,
                                        [subKey]: subQty - 1,
                                      }));
                                    } else {
                                      setIsChecked((prev) => ({
                                        ...prev,
                                        [subKey]: false,
                                      }));
                                      setSelectedQuantities((prev) => ({
                                        ...prev,
                                        [subKey]: 0,
                                      }));
                                    }
                                    markCartSyncDirty();
                                  }}
                                >
                                  −
                                </button>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    minWidth: "20px",
                                    display: "inline-block",
                                    textAlign: "center",
                                  }}
                                >
                                  {subQty}
                                </span>
                                <button
                                  className="button-size"
                                  onClick={() => {
                                    const maxQty = sub.qty_available || 999;
                                    if (subQty < maxQty) {
                                      setSelectedQuantities((prev) => ({
                                        ...prev,
                                        [subKey]: subQty + 1,
                                      }));
                                      markCartSyncDirty();
                                    }
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            )}
                            <label htmlFor={subKey}>
                              {sub.item_name}
                            </label>
                            <p>{formatCOP(sub.price)}</p>
                          </div>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                )}

                <div style={{ marginTop: "12px" }}>
                  <button
                    onClick={() => removeFromCart(item.item_id)}
                    aria-label="Quitar del carrito"
                  >
                    <FontAwesomeIcon icon={faTrash} /> Eliminar
                  </button>
                </div>
              </div>
            );
          })}
          </div>{/* cart-items-scroll */}

          {/* Cart Footer: total + botón siempre visibles */}
          {cart.length > 0 && (
            <div className="cart-footer">
              <div className="total-cart">
                <p>Total: {formattedTotal}</p>
              </div>
              {error && (
                <div
                  style={{
                    color: "var(--color-danger)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 500,
                    textAlign: "center",
                    padding: "12px",
                    background: "rgba(239, 68, 68, 0.08)",
                    borderRadius: "8px",
                    marginBottom: "12px",
                  }}
                >
                  Debe elegir una opción en los campos requeridos.
                </div>
              )}
              {minimumOrderError && (
                <div
                  style={{
                    color: "var(--color-danger)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 500,
                    textAlign: "center",
                    padding: "12px",
                    background: "rgba(239, 68, 68, 0.08)",
                    borderRadius: "8px",
                    marginBottom: "12px",
                  }}
                >
                  {MessageValorTotal()}
                </div>
              )}
              {addonStockMessage && (
                <div
                  style={{
                    color: "var(--color-danger)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 500,
                    textAlign: "center",
                    padding: "12px",
                    background: "rgba(239, 68, 68, 0.08)",
                    borderRadius: "8px",
                    marginBottom: "12px",
                  }}
                >
                  {addonStockMessage}
                </div>
              )}
              {cartStockErrorMessage && (
                <div
                  style={{
                    color: "var(--color-danger)",
                    fontSize: "var(--font-size-sm)",
                    fontWeight: 500,
                    textAlign: "center",
                    padding: "12px",
                    background: "rgba(239, 68, 68, 0.08)",
                    borderRadius: "8px",
                    marginBottom: "12px",
                  }}
                >
                  {cartStockErrorMessage}
                </div>
              )}
              {isAutoSyncingCart && !isSubmittingOrder && (
                <div
                  style={{
                    color: "var(--color-text-secondary)",
                    fontSize: "var(--font-size-sm)",
                    textAlign: "center",
                    marginBottom: "12px",
                  }}
                >
                  Sincronizando cambios del carrito...
                </div>
              )}
              <button
                className={`checkout-btn ${isSubmittingOrder ? "is-processing" : ""} ${hasConfirmedWhatsAppOrder ? "is-completed" : ""}`}
                onClick={InitOrderTemporary}
                disabled={isSubmittingOrder || hasConfirmedWhatsAppOrder}
              >
                <FontAwesomeIcon icon={faWhatsapp} />
                {isSubmittingOrder
                  ? "Procesando pedido..."
                  : hasConfirmedWhatsAppOrder
                    ? "Pedido enviado por WhatsApp"
                    : "Iniciar pedido por WhatsApp"}
              </button>
            </div>
          )}
        </Modal>
        <Modal
          isOpen={Boolean(selectedImageProduct)}
          onRequestClose={closeImageModal}
          style={productImageModalStyles}
          contentLabel="Imagen ampliada del producto"
        >
          {selectedImageProduct && (
            <div className="product-image-modal">
              <button
                type="button"
                className="product-image-modal-close"
                onClick={closeImageModal}
                aria-label="Cerrar imagen ampliada"
              >
                x
              </button>
              <div className="product-image-modal-frame">
                <img
                  src={selectedImageProduct.img_src}
                  alt={selectedImageProduct.item_name}
                  className="product-image-modal-img"
                />
              </div>
              <div className="product-image-modal-caption">
                <h3>{selectedImageProduct.item_name}</h3>
                {selectedImageProduct.brand && (
                  <p>{selectedImageProduct.brand}</p>
                )}
              </div>
            </div>
          )}
        </Modal>
      </div>

      {/* ===== Server-Side Pagination ===== */}
      {!isLoading && !isSearchMode && totalPages > 1 && !isMobileView && (
        <div className="pagination">
          <button onClick={goToPrevPage} disabled={!hasPrev}>
            ‹ Anterior
          </button>
          {getPageNumbers().map((number) => (
            <button
              key={number}
              onClick={() => goToPage(number)}
              className={apiPage === number ? "active" : ""}
            >
              {number}
            </button>
          ))}
          <button onClick={goToNextPage} disabled={!hasNext}>
            Siguiente ›
          </button>
        </div>
      )}
      {!isSearchMode && totalPages > 1 && isMobileView && (
        <div className="mobile-load-more">
          {hasNext ? (
            <button
              type="button"
              className="mobile-load-more-btn"
              onClick={loadMoreProducts}
              disabled={isLoading}
            >
              {isLoading ? "Cargando productos..." : "Cargar más productos"}
            </button>
          ) : (
            <p className="mobile-load-more-done">
              Ya viste los {displayProducts.length} productos disponibles.
            </p>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default ProductMenu;
